import { gsap } from 'gsap';

/**
 * Repli mobile des cartes du layout "pricing".
 *
 * Sous 768px, tout ce qui suit le bloc « toggle » est masqué derrière un bouton.
 * Au-delà, le panneau est visible en permanence (classe Tailwind `md:block`) et
 * le bouton est masqué : le module remet alors les styles inline à zéro pour ne
 * pas laisser une hauteur figée par GSAP.
 *
 * Contrat de balisage (cf. views/acf-layouts/pricing.twig) :
 *  - [data-pricing-card]   : la carte, cible du recalage en haut d'écran
 *  - [data-pricing-toggle] : le bouton, porte aria-controls + aria-expanded
 *  - [data-pricing-panel]  : le panneau repliable, ciblé par aria-controls
 *  - .pricing-chevron      : le chevron pivoté à l'ouverture
 */

const MOBILE_QUERY = '(max-width: 767px)';
const GAP_UNDER_HEADER = 12; // respiration sous le menu fixe, en px

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function initPricingAccordion(context = document) {
  const buttons = context.querySelectorAll('[data-pricing-toggle]');
  if (!buttons.length) return;

  const mq = window.matchMedia(MOBILE_QUERY);
  const items = [];

  buttons.forEach((btn) => {
    if (btn.__pricingAccordion) return; // garde contre la double init
    const panelId = btn.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;
    btn.__pricingAccordion = true;

    const item = {
      btn,
      panel,
      card: btn.closest('[data-pricing-card]'),
      chevron: btn.querySelector('.pricing-chevron'),
      open: false,
    };
    items.push(item);

    btn.addEventListener('click', () => {
      if (!mq.matches) return; // sécurité : au-delà de 768px le bouton est masqué
      if (item.open) collapse(item);
      else expand(item);
    });
  });

  if (!items.length) return;

  const sync = () => {
    items.forEach((item) => (mq.matches ? collapseInstant(item) : releaseForDesktop(item)));
  };

  sync();

  // Safari < 14 n'a pas addEventListener sur MediaQueryList
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', sync);
  else if (typeof mq.addListener === 'function') mq.addListener(sync);
}

function expand(item) {
  const { panel, chevron, btn } = item;
  gsap.killTweensOf(panel);
  panel.classList.remove('hidden');

  const target = panel.scrollHeight;
  const duration = prefersReducedMotion() ? 0 : 0.45;

  gsap
    .timeline()
    .set(panel, { height: 0, opacity: 0, overflow: 'hidden' })
    .to(panel, { height: target, duration, ease: 'power2.out' })
    .to(panel, { opacity: 1, duration: duration ? 0.3 : 0, ease: 'power2.out' }, duration ? 0.05 : 0)
    .add(() => {
      // Hauteur libérée : le contenu peut grandir (images, wysiwyg) sans être coupé
      panel.style.height = 'auto';
      panel.style.overflow = 'visible';
    });

  rotate(chevron, 180);
  btn.setAttribute('aria-expanded', 'true');
  item.open = true;

  scrollCardUnderHeader(item.card);
}

function collapse(item) {
  const { panel, chevron, btn } = item;
  gsap.killTweensOf(panel);

  const current = panel.getBoundingClientRect().height;
  const duration = prefersReducedMotion() ? 0 : 0.45;

  gsap
    .timeline()
    .set(panel, { height: current, overflow: 'hidden' })
    .to(panel, { height: 0, opacity: 0, duration, ease: 'power2.inOut' })
    .add(() => {
      panel.classList.add('hidden');
      panel.style.height = '0px';
    });

  rotate(chevron, 0);
  btn.setAttribute('aria-expanded', 'false');
  item.open = false;
}

/** État replié sans animation (init, ou retour depuis le desktop). */
function collapseInstant(item) {
  const { panel, chevron, btn } = item;
  gsap.killTweensOf(panel);
  panel.classList.add('hidden');
  panel.style.height = '0px';
  panel.style.opacity = '0';
  panel.style.overflow = 'hidden';
  gsap.set(chevron, { rotation: 0 });
  btn.setAttribute('aria-expanded', 'false');
  item.open = false;
}

/**
 * Au-delà de 768px, `md:block` rend le panneau visible : il faut purger les
 * styles inline laissés par GSAP, sinon la carte garde une hauteur de 0.
 */
function releaseForDesktop(item) {
  const { panel, chevron, btn } = item;
  gsap.killTweensOf(panel);
  panel.classList.remove('hidden');
  panel.style.height = '';
  panel.style.opacity = '';
  panel.style.overflow = '';
  gsap.set(chevron, { rotation: 0 });
  btn.setAttribute('aria-expanded', 'false');
  item.open = false;
}

function rotate(el, deg) {
  if (!el) return;
  gsap.to(el, { rotation: deg, duration: prefersReducedMotion() ? 0 : 0.3, ease: 'power2.out' });
}

/**
 * Recale la carte ouverte en haut de l'écran, sous le menu fixe.
 * La hauteur du header est mesurée au moment du clic : sous 768px la barre
 * d'avis est masquée (`hidden md:flex`), et headerScroll.js peut avoir translaté
 * l'ensemble — on lit donc la géométrie réelle plutôt qu'une constante.
 */
function scrollCardUnderHeader(card) {
  if (!card) return;
  const header = document.querySelector('#site-header');
  const headerHeight = header ? header.getBoundingClientRect().height : 0;
  const top = window.scrollY + card.getBoundingClientRect().top - headerHeight - GAP_UNDER_HEADER;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}
